import { DialogComponent } from '@theme/dialog';
import { buildReport, fetchSystemReport, fetchSystems, isConfigured, primeFromPayload } from '@theme/water-report';

/**
 * localStorage key holding the shopper's ZIP code.
 *
 * Exported alongside the accessors below so the EWG data layer — and anything else
 * that needs the ZIP (a water-report page, a product recommender) — reads and writes
 * the same slot instead of each keeping its own copy.
 */
export const WATER_ZIP_KEY = 'cf-water-zip';

/** @returns {string} The stored ZIP, or '' when none is saved or storage is blocked. */
export function getStoredZip() {
  try {
    const value = localStorage.getItem(WATER_ZIP_KEY) ?? '';
    return /^\d{5}$/.test(value) ? value : '';
  } catch {
    // Safari private browsing and cookie-blocking extensions throw on access.
    return '';
  }
}

/**
 * Persists a ZIP, or clears it when passed a falsy/invalid value.
 * @param {string} zip
 * @returns {string} The value that was stored ('' if cleared).
 */
export function setStoredZip(zip) {
  const next = /^\d{5}$/.test(zip ?? '') ? zip : '';

  try {
    if (next) localStorage.setItem(WATER_ZIP_KEY, next);
    else localStorage.removeItem(WATER_ZIP_KEY);
  } catch {
    // Non-fatal: the drawer still works for the rest of the page view.
  }

  // Same-tab notification. The native `storage` event only fires in other tabs.
  document.dispatchEvent(new CustomEvent(WaterZipChangeEvent.eventName, { detail: { zip: next } }));

  return next;
}

/** Fired on `document` whenever the stored ZIP changes, in any tab. */
export class WaterZipChangeEvent extends CustomEvent {
  static eventName = 'water-zip:change';
}

/**
 * The "What's in your water" side drawer.
 *
 * Owns the ZIP code (read/write/reflect) and every view state. It does NOT fetch:
 * the EWG lookup listens for `water-report:zip` / `water-report:open`, then calls
 * `showLoading()` and hands the normalised payload to `setReport()`.
 *
 * States, set on the host element as `data-state`:
 *   `ready`   — report populated with real data
 *   `empty`   — report chrome with N/A in every slot (no ZIP, or no data for it)
 *   `loading` — skeleton
 *   `zip`     — ZIP capture sheet
 *   `error`   — ZIP capture sheet plus a message
 *
 * @typedef {object} WaterReportProvider
 * @property {string} id
 * @property {string} name
 *
 * @typedef {object} WaterReport
 * @property {number} exceedingCount - Contaminants above EWG health guidelines.
 * @property {number} totalCount - Total contaminants detected.
 * @property {number} [removedCount] - How many the filter removes.
 * @property {string} [providerName]
 * @property {string} [populationServed]
 * @property {string} [yearsAvailable]
 * @property {string} [waterSource]
 * @property {WaterReportProvider[]} [providers]
 * @property {string} [selectedProviderId]
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} [gaugeValue]
 * @property {HTMLElement} [totalCount]
 * @property {HTMLElement} [removedCount]
 * @property {HTMLElement} [providerList]
 * @property {HTMLElement} [zipDisplay]
 * @property {HTMLElement} [zipAction]
 * @property {HTMLElement} [zipError]
 * @property {HTMLInputElement} [zipInput]
 *
 * @extends {DialogComponent}
 */
class PdpWaterReportDrawer extends DialogComponent {
  requiredRefs = ['dialog'];

  /** @type {WaterReport | null} */
  #report = null;

  /** State to come back to when the ZIP sheet is dismissed. */
  #previousState = 'empty';

  /** Aborts the in-flight EWG request when a newer one starts. */
  #requestController = /** @type {AbortController | null} */ (null);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(WaterZipChangeEvent.eventName, this.#handleZipChange);
    window.addEventListener('storage', this.#handleStorageEvent);

    this.#reflectZip();

    // The markup ships with the merchant's placeholder numbers so the theme editor
    // and first paint show the designed state. Without a stored ZIP those numbers
    // belong to nobody, so blank them to N/A; with one, leave them for the data
    // layer to overwrite.
    if (!this.zip) this.clearReport();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WaterZipChangeEvent.eventName, this.#handleZipChange);
    window.removeEventListener('storage', this.#handleStorageEvent);
    this.#requestController?.abort();
  }

  /** The stored ZIP, or '' when none is saved. */
  get zip() {
    return getStoredZip();
  }

  /** @param {string} value */
  set zip(value) {
    setStoredZip(value);
  }

  /** The report currently rendered, or null when none has been supplied. */
  get report() {
    return this.#report;
  }

  showDialog() {
    super.showDialog();

    const zip = this.zip;

    // Without a ZIP the drawer stays on the no-data report — the ZIP bar above the
    // provider list is the way in, not a forced interstitial.
    if (!zip) return;

    // Announced whether or not the built-in fetcher runs, so other code can react.
    this.dispatchEvent(new CustomEvent('water-report:open', { bubbles: true, detail: { zip } }));

    // Cached results resolve without a network round trip, so re-opening is cheap.
    this.load(zip);
  }

  /** Puts the drawer into its loading state. */
  showLoading = () => {
    this.#setState('loading');
  };

  /**
   * Shows the ZIP capture sheet.
   * Remembers the state underneath so dismissing returns to it.
   */
  showZipView = () => {
    const current = this.dataset.state;

    if (current !== 'zip' && current !== 'error') this.#previousState = current ?? 'empty';

    if (this.refs.zipInput) this.refs.zipInput.value = this.zip;
    if (this.refs.zipError) this.refs.zipError.textContent = '';

    this.#setState('zip');

    // Only when the shopper asked for this view from an already-open drawer. On
    // open, showModal() has not run yet (it is deferred a frame) so focusing here
    // would be dropped anyway — and auto-raising the keyboard on mobile is worse.
    if (this.refs.dialog?.open) this.refs.zipInput?.focus();
  };

  /** Dismisses the ZIP sheet without saving. */
  hideZipView = () => {
    this.#setState(this.#previousState === 'zip' ? 'empty' : this.#previousState);
  };

  /**
   * Clears every report value back to the no-data placeholder.
   * Also used when a ZIP returns no coverage.
   */
  clearReport = () => {
    const placeholder = this.dataset.noDataValue || 'N/A';

    this.#report = null;
    this.#setText('gaugeValue', placeholder);

    for (const name of ['provider', 'population', 'years', 'source']) {
      this.#setStat(name, placeholder);
    }

    this.refs.providerList?.replaceChildren();
    this.refs.dialog?.style.setProperty('--pwrd-ratio', '0');
    this.#setState('empty');
  };

  /**
   * Shows the ZIP sheet with an error message.
   * @param {string} [message]
   */
  showError = (message) => {
    // #previousState is left alone on purpose: a failed refresh should still be able
    // to fall back to whatever report was on screen before it.
    this.#setState('error');
    if (this.refs.zipError) this.refs.zipError.textContent = message ?? '';
  };

  /**
   * Renders a report and switches the drawer to its ready state.
   * @param {WaterReport} report
   */
  setReport = (report) => {
    this.#report = report;

    this.#setText('gaugeValue', report.exceedingCount);
    this.#setText('totalCount', this.#template('total', report.totalCount));
    this.#setText('removedCount', this.#template('removed', report.removedCount));

    this.#setStat('provider', report.providerName);
    this.#setStat('population', report.populationServed);
    this.#setStat('years', report.yearsAvailable);
    this.#setStat('source', report.waterSource);

    if (Array.isArray(report.providers)) {
      this.#renderProviders(report.providers, report.selectedProviderId);
    }

    this.#setState('ready');
    this.#syncGauge();
  };

  /**
   * Selects a provider chip. The data layer listens for `water-report:provider`
   * on this element to refetch.
   * @param {MouseEvent} event
   */
  selectProvider = (event) => {
    const chip = /** @type {HTMLElement | null} */ (event.target)?.closest('[data-provider-id]');

    if (!(chip instanceof HTMLElement)) return;

    for (const other of this.querySelectorAll('[data-provider-id]')) {
      other.setAttribute('aria-pressed', String(other === chip));
    }

    this.dispatchEvent(
      new CustomEvent('water-report:provider', {
        bubbles: true,
        detail: { providerId: chip.dataset.providerId, providerName: chip.textContent?.trim() },
      })
    );

    if (chip.dataset.providerId) this.load(this.zip, chip.dataset.providerId);
  };

  /**
   * Saves the submitted ZIP and asks the data layer for a report.
   * Deliberately does not enter the loading state itself — the fetcher owns that,
   * so the drawer never sits on a spinner nobody is going to resolve.
   * @param {SubmitEvent} event
   */
  submitZip = (event) => {
    event.preventDefault();

    const zip = this.refs.zipInput?.value.trim() ?? '';

    if (!/^\d{5}$/.test(zip)) {
      if (this.refs.zipError) this.refs.zipError.textContent = 'Enter a valid 5-digit ZIP code.';
      this.refs.zipInput?.focus();
      return;
    }

    if (this.refs.zipError) this.refs.zipError.textContent = '';

    this.zip = zip;
    this.#setState(this.#report ? 'ready' : 'empty');

    this.dispatchEvent(new CustomEvent('water-report:zip', { bubbles: true, detail: { zip } }));

    this.load(zip);
  };

  /**
   * Fetches and renders a report. Safe to call repeatedly: a newer call supersedes
   * any in flight, so quickly switching provider chips cannot land results out of
   * order. No-ops until an EWG key is configured, which leaves the drawer in the
   * no-data state instead of showing a spinner that never resolves.
   *
   * @param {string} zip
   * @param {string} [pws] - Provider to report on. Defaults to the ZIP's first system.
   */
  load = async (zip, pws) => {
    if (!zip || !isConfigured()) return;

    this.#requestController?.abort();

    const controller = new AbortController();
    this.#requestController = controller;
    const { signal } = controller;

    // Refreshing an existing report keeps it on screen and just marks it stale.
    // Swapping in the skeleton would tear down the provider chips mid-interaction,
    // so the chip the shopper just pressed would vanish under the cursor.
    if (this.#report) this.#setBusy(true);
    else this.showLoading();

    try {
      const systems = await fetchSystems(zip, { signal });
      const system = (pws && systems.find((entry) => String(entry.PWS) === String(pws))) || systems[0];
      const payload = await fetchSystemReport(zip, String(system.PWS), { signal });

      if (signal.aborted) return;

      await primeFromPayload(payload);
      if (signal.aborted) return;

      this.setReport(buildReport(payload, system, systems));
    } catch (error) {
      if (signal.aborted || /** @type {any} */ (error)?.name === 'AbortError') return;

      // Anything unexpected still reaches the console; the shopper sees the message.
      if (/** @type {any} */ (error)?.name !== 'WaterReportError') console.error('[water-report]', error);

      this.showError(
        /** @type {any} */ (error)?.message ?? 'We could not load your water report. Please try again.'
      );
    } finally {
      if (this.#requestController === controller) {
        this.#requestController = null;
        this.#setBusy(false);
      }
    }
  };

  /**
   * Marks the visible report as being refreshed. Only the data regions dim — the
   * provider chips stay lit and clickable so switching again does not have to wait.
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.toggleAttribute('data-busy', busy);
    this.refs.report?.setAttribute('aria-busy', String(busy));
  }

  /** @param {string} state */
  #setState(state) {
    this.dataset.state = state;
  }

  /** Keeps the ZIP bar in step with storage — same tab. */
  #handleZipChange = () => {
    this.#reflectZip();
  };

  /**
   * Keeps the ZIP bar in step with storage — other tabs.
   * @param {StorageEvent} event
   */
  #handleStorageEvent = (event) => {
    if (event.key !== WATER_ZIP_KEY && event.key !== null) return;
    this.#reflectZip();
  };

  /** Writes the stored ZIP into the ZIP bar and the input. */
  #reflectZip() {
    const zip = this.zip;
    const { zipDisplay, zipAction, zipInput } = this.refs;

    if (zipDisplay instanceof HTMLElement) {
      zipDisplay.textContent = zip
        ? (this.dataset.zipSavedLabel ?? '[zip]').replace('[zip]', zip)
        : (this.dataset.zipEmptyLabel ?? '');
    }

    if (zipAction instanceof HTMLElement) {
      zipAction.textContent = zip
        ? (this.dataset.zipUpdateAction ?? '')
        : (this.dataset.zipAddAction ?? '');
    }

    if (zipInput instanceof HTMLInputElement) zipInput.value = zip;
  }

  /** Drives the arc length from the exceeding/total ratio. */
  #syncGauge() {
    const total = Number(this.#report?.totalCount ?? 0);
    const exceeding = Number(this.#report?.exceedingCount ?? 0);
    const ratio = total > 0 ? Math.min(Math.max(exceeding / total, 0), 1) : 0;

    this.refs.dialog?.style.setProperty('--pwrd-ratio', String(ratio));
  }

  /**
   * @param {'total' | 'removed'} key
   * @param {number | undefined} count
   */
  #template(key, count) {
    const template = key === 'total' ? this.dataset.totalTemplate : this.dataset.removedTemplate;

    if (count == null) return '';

    return (template ?? '[count]').replace('[count]', String(count));
  }

  /**
   * @param {keyof Refs} ref
   * @param {string | number | undefined} value
   */
  #setText(ref, value) {
    const element = this.refs[ref];

    if (element instanceof HTMLElement && value != null && value !== '') {
      element.textContent = String(value);
    }
  }

  /**
   * @param {string} name - Value of the cell's `data-stat` attribute.
   * @param {string | undefined} value
   */
  #setStat(name, value) {
    if (value == null || value === '') return;

    const cell = this.querySelector(`[data-stat='${name}'] .pwrd__stat-value`);

    if (cell instanceof HTMLElement) cell.textContent = value;
  }

  /**
   * @param {WaterReportProvider[]} providers
   * @param {string | undefined} selectedId
   */
  #renderProviders(providers, selectedId) {
    const list = this.refs.providerList;

    if (!(list instanceof HTMLElement)) return;

    const existing = /** @type {HTMLElement[]} */ ([...list.querySelectorAll('[data-provider-id]')]);
    const unchanged =
      existing.length === providers.length &&
      existing.every((chip, index) => chip.dataset.providerId === providers[index].id);

    // Switching provider returns the same list for the ZIP. Rebuilding it would
    // discard focus and restart the chips' transitions, so only the pressed state
    // moves when the roster is identical.
    if (unchanged) {
      for (const chip of existing) {
        chip.setAttribute('aria-pressed', String(chip.dataset.providerId === selectedId));
      }

      return;
    }

    list.replaceChildren(
      ...providers.map((provider) => {
        const chip = document.createElement('button');

        chip.type = 'button';
        chip.className = 'pwrd__provider';
        chip.dataset.providerId = provider.id;
        chip.setAttribute('aria-pressed', String(provider.id === selectedId));
        chip.setAttribute('on:click', '/selectProvider');
        chip.textContent = provider.name;

        return chip;
      })
    );
  }
}

if (!customElements.get('pdp-water-report-drawer')) {
  customElements.define('pdp-water-report-drawer', PdpWaterReportDrawer);
}
